import { matchPath } from "react-router-dom";
import type { BreadcrumbItem } from "Helpers/generalBreadcrumbs";

type BreadcrumbRule = {
  path: string;
  crumbs: (params: Record<string, string | undefined>) => BreadcrumbItem[];
};

const items = (): BreadcrumbItem => ({
  labelKey: "Items",
});

/** Items sidebar module routes — more specific paths first. */
const ITEMS_BREADCRUMB_RULES: BreadcrumbRule[] = [
  // Parts
  {
    path: "/parts",
    crumbs: () => [items(), { labelKey: "items" }],
  },
  {
    path: "/part/create",
    crumbs: () => [
      items(),
      { labelKey: "items", href: "/parts" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/part/assign/unit/:id",
    crumbs: () => [
      items(),
      { labelKey: "items", href: "/parts" },
      { labelKey: "assignUnit" },
    ],
  },
  {
    path: "/part/assign/model/:id",
    crumbs: () => [
      items(),
      { labelKey: "items", href: "/parts" },
      { labelKey: "assignModel" },
    ],
  },
  {
    path: "/part/assign/supplier/:id",
    crumbs: () => [
      items(),
      { labelKey: "items", href: "/parts" },
      { labelKey: "assignSupplier" },
    ],
  },
  {
    path: "/part/assign/price-list/:id",
    crumbs: () => [
      items(),
      { labelKey: "items", href: "/parts" },
      { labelKey: "assignPriceList" },
    ],
  },
  {
    path: "/part/history/:id",
    crumbs: () => [
      items(),
      { labelKey: "items", href: "/parts" },
      { labelKey: "history" },
    ],
  },
  {
    path: "/part/assign/store/:id",
    crumbs: () => [
      items(),
      { labelKey: "items", href: "/parts" },
      { labelKey: "assignStore" },
    ],
  },
  {
    path: "/part/:id",
    crumbs: () => [
      items(),
      { labelKey: "items", href: "/parts" },
      { labelKey: "Details" },
    ],
  },

  // Services
  {
    path: "/services",
    crumbs: () => [items(), { labelKey: "services" }],
  },
  {
    path: "/service/create",
    crumbs: () => [
      items(),
      { labelKey: "services", href: "/services" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/service/assign/part/:id",
    crumbs: () => [
      items(),
      { labelKey: "services", href: "/services" },
      { labelKey: "assignPart" },
    ],
  },
  {
    path: "/service/assign/vehicle-type/:id",
    crumbs: () => [
      items(),
      { labelKey: "services", href: "/services" },
      { labelKey: "assignVehicleType" },
    ],
  },
  {
    path: "/service/assign/station/:id",
    crumbs: () => [
      items(),
      { labelKey: "services", href: "/services" },
      { labelKey: "assignStation" },
    ],
  },
  {
    path: "/service/assign/model/:id",
    crumbs: () => [
      items(),
      { labelKey: "services", href: "/services" },
      { labelKey: "assignModel" },
    ],
  },
  {
    path: "/service/assign/price-list/:id",
    crumbs: () => [
      items(),
      { labelKey: "services", href: "/services" },
      { labelKey: "assignPriceList" },
    ],
  },
  {
    path: "/service/:id",
    crumbs: () => [
      items(),
      { labelKey: "services", href: "/services" },
      { labelKey: "Details" },
    ],
  },

  // Packages
  {
    path: "/packages",
    crumbs: () => [items(), { labelKey: "packages" }],
  },
  {
    path: "/package/create",
    crumbs: () => [
      items(),
      { labelKey: "packages", href: "/packages" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/package/assign/service/:id",
    crumbs: () => [
      items(),
      { labelKey: "packages", href: "/packages" },
      { labelKey: "assignService" },
    ],
  },
  {
    path: "/package/assign/part/:id",
    crumbs: () => [
      items(),
      { labelKey: "packages", href: "/packages" },
      { labelKey: "assignPart" },
    ],
  },
  {
    path: "/package/assign/station/:id",
    crumbs: () => [
      items(),
      { labelKey: "packages", href: "/packages" },
      { labelKey: "assignStation" },
    ],
  },
  {
    path: "/package/assign/model/:id",
    crumbs: () => [
      items(),
      { labelKey: "packages", href: "/packages" },
      { labelKey: "assignModel" },
    ],
  },
  {
    path: "/package/assign/price-list/:id",
    crumbs: () => [
      items(),
      { labelKey: "packages", href: "/packages" },
      { labelKey: "assignPriceList" },
    ],
  },
  {
    path: "/package/:id",
    crumbs: () => [
      items(),
      { labelKey: "packages", href: "/packages" },
      { labelKey: "Details" },
    ],
  },

  // Parts group
  {
    path: "/parts-group",
    crumbs: () => [items(), { labelKey: "itemsGroup" }],
  },
  {
    path: "/part-group/create",
    crumbs: () => [
      items(),
      { labelKey: "itemsGroup", href: "/parts-group" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/part-group/assign/part/:id",
    crumbs: () => [
      items(),
      { labelKey: "itemsGroup", href: "/parts-group" },
      { labelKey: "assignPart" },
    ],
  },
  {
    path: "/part-group/:id",
    crumbs: () => [
      items(),
      { labelKey: "itemsGroup", href: "/parts-group" },
      { labelKey: "Details" },
    ],
  },

  // Services group
  {
    path: "/services-group",
    crumbs: () => [items(), { labelKey: "servicesGroup" }],
  },
  {
    path: "/service-group/create",
    crumbs: () => [
      items(),
      { labelKey: "servicesGroup", href: "/services-group" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/service-group/assign/service/:id",
    crumbs: () => [
      items(),
      { labelKey: "servicesGroup", href: "/services-group" },
      { labelKey: "assignService" },
    ],
  },
  {
    path: "/service-group/assign/technician/:id",
    crumbs: () => [
      items(),
      { labelKey: "servicesGroup", href: "/services-group" },
      { labelKey: "assignTechnician" },
    ],
  },
  {
    path: "/service-group/:id",
    crumbs: () => [
      items(),
      { labelKey: "servicesGroup", href: "/services-group" },
      { labelKey: "Details" },
    ],
  },

  // Packages group
  {
    path: "/packages-group",
    crumbs: () => [items(), { labelKey: "packagesGroup" }],
  },
  {
    path: "/package-group/create",
    crumbs: () => [
      items(),
      { labelKey: "packagesGroup", href: "/packages-group" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/package-group/assign/package/:id",
    crumbs: () => [
      items(),
      { labelKey: "packagesGroup", href: "/packages-group" },
      { labelKey: "assignPackage" },
    ],
  },
  {
    path: "/package-group/:id",
    crumbs: () => [
      items(),
      { labelKey: "packagesGroup", href: "/packages-group" },
      { labelKey: "Details" },
    ],
  },

  // Part categories
  {
    path: "/part-categories",
    crumbs: () => [items(), { labelKey: "itemsCategory" }],
  },
  {
    path: "/part-category/create",
    crumbs: () => [
      items(),
      { labelKey: "itemsCategory", href: "/part-categories" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/part-category/:id",
    crumbs: () => [
      items(),
      { labelKey: "itemsCategory", href: "/part-categories" },
      { labelKey: "Details" },
    ],
  },

  // Price lists
  {
    path: "/price-lists",
    crumbs: () => [items(), { labelKey: "priceLists" }],
  },
  {
    path: "/price-list/create",
    crumbs: () => [
      items(),
      { labelKey: "priceLists", href: "/price-lists" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/price-list/assign/package/:id",
    crumbs: () => [
      items(),
      { labelKey: "priceLists", href: "/price-lists" },
      { labelKey: "assignPackage" },
    ],
  },
  {
    path: "/price-list/assign/service/:id",
    crumbs: () => [
      items(),
      { labelKey: "priceLists", href: "/price-lists" },
      { labelKey: "assignService" },
    ],
  },
  {
    path: "/price-list/assign/part/:id",
    crumbs: () => [
      items(),
      { labelKey: "priceLists", href: "/price-lists" },
      { labelKey: "assignPart" },
    ],
  },
  {
    path: "/price-list/:id",
    crumbs: () => [
      items(),
      { labelKey: "priceLists", href: "/price-lists" },
      { labelKey: "Details" },
    ],
  },
];

export const getItemsBreadcrumbs = (
  pathname: string,
): BreadcrumbItem[] | null => {
  for (const rule of ITEMS_BREADCRUMB_RULES) {
    const match = matchPath({ path: rule.path, end: true }, pathname);
    if (match) {
      return rule.crumbs(match.params as Record<string, string | undefined>);
    }
  }
  return null;
};
